import { Link } from 'react-router-dom';
import { Lightning, Barbell, Heart, ChartLineUp, Brain, Users, ArrowRight, Fire, NumberCircleOne, NumberCircleTwo, NumberCircleThree, Star } from '@phosphor-icons/react';

export default function LandingPage() {
  const features = [
    { icon: Barbell, title: "AI-POWERED PLANS", desc: "Personalized workout programs generated by AI, tailored to your goals and fitness level." },
    { icon: Heart, title: "STRESS RESET", desc: "2-5 minute breathing and mobility sessions designed to reduce stress and boost energy." },
    { icon: ChartLineUp, title: "TRACK PROGRESS", desc: "Visual dashboards showing streaks, workout history, mood patterns, and strength gains." },
    { icon: Brain, title: "SMART NUTRITION", desc: "AI-generated meal frameworks that are simple, budget-friendly, and culturally diverse." },
    { icon: Fire, title: "HABIT SYSTEM", desc: "Streaks, mood logging, and energy tracking to build lasting fitness habits." },
    { icon: Users, title: "CHALLENGES", desc: "Join 7 to 28-day challenges. Push yourself with structured progressive programs." },
  ];

  const steps = [
    { num: NumberCircleOne, title: "SET YOUR GOALS", desc: "Tell us your fitness level, target areas, and what you want to achieve." },
    { num: NumberCircleTwo, title: "GET YOUR PLAN", desc: "AI generates a personalized workout plan with form tips and modifications." },
    { num: NumberCircleThree, title: "TRAIN & TRACK", desc: "Follow guided workouts, log your mood, and watch your streaks grow." },
  ];

  const testimonials = [
    { name: "Jordan C.", rating: 5, text: "As a 19-year-old and a definite beginner, this app has been incredible. After 3 months my body is completely different.", badge: "3-month user" },
    { name: "Priya S.", rating: 5, text: "The stress reset sessions are a game-changer. I use the 2-minute breathing reset before work meetings and it genuinely helps.", badge: "Daily user" },
    { name: "Marcus T.", rating: 5, text: "No equipment, no excuses. The AI plans actually adapt to my level and the form tips have helped me avoid injury.", badge: "28-day challenger" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning weight="fill" className="text-[#FF3B30] w-7 h-7" />
            <span className="font-['Barlow_Condensed'] text-xl font-black uppercase tracking-tight">GRIT.FIT</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/auth" data-testid="nav-login-button" className="btn-secondary px-5 py-2 text-sm">Sign In</Link>
            <Link to="/auth" data-testid="nav-signup-button" className="btn-primary px-5 py-2 text-sm">Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1760084081757-6f918c08403b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxwZXJzb24lMjBib2R5d2VpZ2h0JTIwd29ya291dCUyMGhvbWV8ZW58MHx8fHwxNzc1OTQ2MDA0fDA&ixlib=rb-4.1.0&q=85"
            alt="Home workout"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="label-tactical text-[#FF3B30] mb-4 animate-fade-in-up">No Equipment Required</div>
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.85] mb-6 animate-fade-in-up animate-delay-1">
              TRAIN<br />ANYWHERE.<br />
              <span className="text-[#FF3B30]">ZERO GEAR.</span>
            </h1>
            <p className="text-xl text-[#A1A1AA] mb-10 max-w-lg animate-fade-in-up animate-delay-2">
              AI-powered bodyweight workouts, personalized nutrition, and habit tracking.
              Everything you need to get fit — nothing you don't.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-in-up animate-delay-3">
              <Link to="/auth" data-testid="hero-cta-button" className="btn-primary px-8 py-4 text-lg flex items-center gap-2">
                Start Free <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <span className="text-sm text-[#71717A]">No credit card. No equipment. No excuses.</span>
            </div>
            <div className="flex items-center gap-8 mt-12 animate-fade-in-up animate-delay-4">
              <div>
                <div className="text-3xl font-black font-['Barlow_Condensed']">200+</div>
                <div className="text-xs uppercase tracking-widest text-[#71717A]">Exercises</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-3xl font-black font-['Barlow_Condensed']">28-DAY</div>
                <div className="text-xs uppercase tracking-widest text-[#71717A]">Programs</div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <div className="text-3xl font-black font-['Barlow_Condensed']">AI</div>
                <div className="text-xs uppercase tracking-widest text-[#71717A]">Powered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 border-t border-white/10" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="label-tactical text-[#FF3B30] mb-3">How It Works</div>
            <h2 className="text-5xl font-black tracking-tighter">3 STEPS TO FITNESS</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={s.title} className="text-center">
                <s.num weight="fill" className="w-16 h-16 text-[#FF3B30] mx-auto mb-5" />
                <h3 className="text-2xl font-bold tracking-tight mb-3">{s.title}</h3>
                <p className="text-[#A1A1AA] max-w-xs mx-auto">{s.desc}</p>
                {i < 2 && <div className="hidden md:block w-full h-px bg-white/5 mt-8" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 border-t border-white/10" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="label-tactical text-[#FF3B30] mb-3">Why GRIT.FIT</div>
            <h2 className="text-5xl font-black tracking-tighter">BUILT DIFFERENT</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.title} className="tactical-card p-6 hover:border-white/20 transition-all duration-200 group">
                <f.icon weight="bold" className="w-10 h-10 text-[#FF3B30] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold tracking-tight mb-2">{f.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-white/10" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="label-tactical text-[#FF3B30] mb-3">Real Results</div>
            <h2 className="text-5xl font-black tracking-tighter">WHAT USERS SAY</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <div key={t.name} className="tactical-card p-6" data-testid={`testimonial-${t.name}`}>
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={`star-${j}`} weight="fill" className="w-4 h-4 text-[#FF9500]" />
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{t.name}</span>
                  <span className="text-xs bg-[#FF3B30]/10 text-[#FF3B30] px-2 py-0.5 font-bold">{t.badge}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/10 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-6">
            READY TO<br /><span className="text-[#FF3B30]">TRANSFORM?</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-10">
            Join thousands who are getting fit at home with zero equipment.
            Start your journey today — it's completely free.
          </p>
          <Link to="/auth" data-testid="cta-button" className="btn-primary px-10 py-4 text-lg inline-flex items-center gap-2">
            Get Started Free <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Lightning weight="fill" className="text-[#FF3B30] w-5 h-5" />
            <span className="font-['Barlow_Condensed'] text-sm font-bold uppercase tracking-tight text-[#71717A]">GRIT.FIT</span>
          </div>
          <p className="text-[#71717A] text-sm hidden sm:block">Built for the grind. No shortcuts.</p>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/changelog" className="text-[#71717A] hover:text-white transition-colors" data-testid="footer-changelog">What's New</Link>
            <Link to="/contact" className="text-[#71717A] hover:text-white transition-colors" data-testid="footer-contact">Contact</Link>
            <Link to="/privacy" className="text-[#71717A] hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="text-[#71717A] hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
